import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { API_URL } from "../../config";
import "./css/FollowUps.css";

const RECENT_SALES_VISIT_APIS = [
    `${API_URL}/api/get-sales-customer-visit-reports`,
    `${API_URL}/api/get-recent-sales-visits`,
    `${API_URL}/get-recent-sales-visits`,
];

function FollowUps({ authInfo }) {
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        const headers = authInfo?.token
            ? {
                  "auth-token": authInfo.token,
                  Authorization: `Bearer ${authInfo.token}`,
              }
            : {};

        const run = async () => {
            setLoading(true);
            setError("");
            try {
                const response = await fetchRecentSalesVisitsData(headers, 300);
                setVisits(extractRecentVisits(response.data));
            } catch (err) {
                setVisits([]);
                setError(err?.message || "Failed to load follow-up data.");
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [authInfo?.token]);

    const rows = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return visits
            .map((item) => {
                const dueDateRaw = item.next_followup_date || item.visit_date;
                const dueDate = new Date(dueDateRaw || "");
                const isValid = !Number.isNaN(dueDate.getTime());
                if (isValid) dueDate.setHours(0, 0, 0, 0);

                const rawStatus = String(item.status || "").toLowerCase();
                const isCompleted = rawStatus.includes("complete");
                const isScheduled = rawStatus.includes("schedule");
                const status = isCompleted
                    ? "completed"
                    : isScheduled
                      ? "overdue"
                      : isValid && dueDate < today
                        ? "overdue"
                        : "scheduled";

                const priorityRaw = String(
                    item.customer_priority || item.priority || item.followup_priority || "-"
                ).trim();

            return {
                id: item.id || item.visit_id || `${item.customer_name}-${item.next_followup_date || item.visit_date}`,
                customer: item.customer_name || "-",
                salesperson: item.sales_person_name || item.sales_person || item.user_full_name || "-",
                salespersonArea: extractSalespersonArea(item),
                dueDate: formatDateOnly(dueDateRaw),
                dueDateRaw,
                    priority: priorityRaw || "-",
                    status,
                    rawVisit: item,
                    location:
                        item.location ||
                        item.visit_location ||
                        item.customer_area ||
                        item.customer_address ||
                        "-",
                    notes:
                        item.visit_notes ||
                        item.previous_feedback ||
                        item.previous_plan_feedback ||
                        item.notes ||
                        item.followup_note ||
                        item.note ||
                        "No notes added.",
                };
            })
            .filter((row) => (statusFilter === "all" ? true : row.status === statusFilter))
            .sort((a, b) => {
                const aTime = dateToTime(a.dueDate);
                const bTime = dateToTime(b.dueDate);
                return aTime - bTime;
            });
    }, [visits, statusFilter]);

    return (
        <div className="fuPage">
            <div className="fuHead">
                <div>
                    <h1>Follow-Ups</h1>
                    <p>Welcome back! Here&apos;s what&apos;s happening today.</p>
                </div>
            </div>

            <div className="fuCard">
                <div className="fuCardHead">
                    <h3>Follow-Ups</h3>
                    <select
                        className="fuFilter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All</option>
                        <option value="completed">Completed</option>
                        <option value="overdue">Overdue</option>
                    </select>
                </div>

                {error ? <div className="fuError">{error}</div> : null}
                {loading ? (
                    <p className="fuEmpty">Loading...</p>
                ) : rows.length === 0 ? (
                    <p className="fuEmpty">No follow-up data found.</p>
                ) : (
                    <div className="fuTableWrap">
                        <table className="fuTable">
                            <thead>
                                <tr>
                                    <th>Customer</th>
                                    <th>Salesperson</th>
                                    <th>Due Date</th>
                                    <th>Priority</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={`fu-row-${row.id}`}>
                                        <td>{row.customer}</td>
                                        <td>{row.salesperson}</td>
                                        <td>{row.dueDate}</td>
                                        <td>
                                            <span className={`fuPill fuPriority ${priorityClass(row.priority)}`}>
                                                {row.priority}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`fuPill fuStatus ${statusClass(row.status)}`}>
                                                {capitalize(row.status)}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="fuDetailsBtn"
                                                onClick={() => setSelectedItem(row)}
                                            >
                                                View Details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {selectedItem ? (
                <div className="fuModalOverlay" onClick={() => setSelectedItem(null)}>
                    <div className="fuModalCard" onClick={(e) => e.stopPropagation()}>
                        <div className="fuModalHead">
                            <h3>Visit Details</h3>
                            <button type="button" className="fuCloseBtn" onClick={() => setSelectedItem(null)}>
                                ×
                            </button>
                        </div>

                        <div className="fuModalGrid">
                            <div>
                                <p className="fuLabel">Customer</p>
                                <strong>{selectedItem.customer}</strong>
                            </div>
                            <div>
                                <p className="fuLabel">Salesperson</p>
                                <strong>{selectedItem.salesperson}</strong>
                            </div>
                            <div>
                                <p className="fuLabel">Sales Person Area</p>
                                <strong>{selectedItem.salespersonArea || "-"}</strong>
                            </div>
                            <div>
                                <p className="fuLabel">Visit Date</p>
                                <strong>{formatVisitDateTime(selectedItem.dueDateRaw)}</strong>
                            </div>
                            <div>
                                <p className="fuLabel">Status</p>
                                <strong>{capitalize(selectedItem.status)}</strong>
                            </div>
                            <div className="fuFull">
                                <p className="fuLabel">Location</p>
                                <strong>{selectedItem.location}</strong>
                            </div>
                            <div>
                                <p className="fuLabel">Customer Phone</p>
                                <strong>{selectedItem.rawVisit?.customer_phone || selectedItem.rawVisit?.customer_mobile_no || "-"}</strong>
                            </div>
                            <div>
                                <p className="fuLabel">Customer Type</p>
                                <strong>{selectedItem.rawVisit?.customer_type || "-"}</strong>
                            </div>
                            <div>
                                <p className="fuLabel">Last PO Reference</p>
                                <strong>{selectedItem.rawVisit?.last_po_reference || "-"}</strong>
                            </div>
                            <div>
                                <p className="fuLabel">Competitor</p>
                                <strong>{selectedItem.rawVisit?.competitor || "-"}</strong>
                            </div>
                            <div>
                                <p className="fuLabel">Product Category</p>
                                <strong>
                                    {selectedItem.rawVisit?.product_category_name ||
                                        selectedItem.rawVisit?.product_category ||
                                        selectedItem.rawVisit?.product_category_id ||
                                        "-"}
                                </strong>
                            </div>
                            <div>
                                <p className="fuLabel">Customer Priority</p>
                                <strong>{selectedItem.rawVisit?.customer_priority || "-"}</strong>
                            </div>
                            <div>
                                <p className="fuLabel">Visit Agenda</p>
                                <strong>{selectedItem.rawVisit?.visit_agenda || "-"}</strong>
                            </div>
                            <div>
                                <p className="fuLabel">Next Follow-up Date</p>
                                <strong>{formatDateOnly(selectedItem.rawVisit?.next_followup_date)}</strong>
                            </div>
                            <div>
                                <p className="fuLabel">Assigned Salesperson ID</p>
                                <strong>{selectedItem.rawVisit?.assigned_sales_person_id || "-"}</strong>
                            </div>
                        </div>

                        <div className="fuSection">
                            <h4>Visit Notes</h4>
                            <div className="fuNotesBox">{selectedItem.notes}</div>
                        </div>

                        <div className="fuSection">
                            <h4>Action Items</h4>
                            <ul className="fuActionList">
                                {extractVisitActionItems(selectedItem.rawVisit).map((item, idx) => (
                                    <li key={`fu-action-${idx}`}>{item}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="fuSection">
                            <h4>Meeting Persons</h4>
                            <ul className="fuActionList">
                                {extractMeetingPersons(selectedItem.rawVisit).length > 0 ? (
                                    extractMeetingPersons(selectedItem.rawVisit).map((person, idx) => {
                                        const view = formatMeetingPerson(person);
                                        return (
                                            <li key={`fu-meeting-${idx}`}>
                                                <strong>{view.name}</strong>
                                                {view.details.length > 0 ? (
                                                    <div style={{ marginTop: "4px", fontSize: "14px", color: "#4b5563" }}>
                                                        {view.details.join(" | ")}
                                                    </div>
                                                ) : null}
                                            </li>
                                        );
                                    })
                                ) : (
                                    <li>No meeting person added.</li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function statusClass(status) {
    if (status === "completed") return "fuStCompleted";
    if (status === "overdue") return "fuStOverdue";
    return "fuStScheduled";
}

function priorityClass(priority) {
    const v = String(priority || "").toLowerCase();
    if (v.includes("high") || v.includes("priority a") || v === "a") return "fuPrHigh";
    if (v.includes("medium") || v.includes("priority b") || v === "b") return "fuPrMedium";
    return "fuPrLow";
}

function capitalize(txt) {
    const v = String(txt || "");
    if (!v) return "-";
    return v.charAt(0).toUpperCase() + v.slice(1);
}

function formatDateOnly(date) {
    if (!date) return "-";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return String(date);
    return parsed.toLocaleDateString("en-GB");
}

function dateToTime(dateTxt) {
    if (!dateTxt || dateTxt === "-") return Number.MAX_SAFE_INTEGER;
    const [d, m, y] = String(dateTxt).split("/");
    const parsed = new Date(`${y}-${m}-${d}`);
    if (Number.isNaN(parsed.getTime())) return Number.MAX_SAFE_INTEGER;
    return parsed.getTime();
}

function formatVisitDateTime(date) {
    if (!date) return "-";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return String(date);
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();
    const hours = String(parsed.getHours()).padStart(2, "0");
    const minutes = String(parsed.getMinutes()).padStart(2, "0");
    const seconds = String(parsed.getSeconds()).padStart(2, "0");
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
}

function extractVisitActionItems(visit) {
    const candidates = [
        visit?.action_items,
        visit?.action_item,
        visit?.follow_up_action,
        visit?.followup_action,
        visit?.follow_up_plan,
        visit?.next_action_plan,
        visit?.next_action,
    ];

    for (const raw of candidates) {
        if (Array.isArray(raw)) {
            const normalized = raw.map((x) => String(x || "").trim()).filter(Boolean);
            if (normalized.length > 0) return normalized;
        }

        if (typeof raw === "string" && raw.trim()) {
            try {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    const normalized = parsed.map((x) => String(x || "").trim()).filter(Boolean);
                    if (normalized.length > 0) return normalized;
                }
            } catch (_e) {
                const normalized = raw
                    .split(/\r?\n|,|;/)
                    .map((x) => x.trim())
                    .filter(Boolean);
                if (normalized.length > 0) return normalized;
            }
        }
    }

    return ["No action item added."];
}

function extractMeetingPersons(visit) {
    const raw = visit?.meeting_persons_details;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") return [raw];
    if (typeof raw === "string" && raw.trim()) {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
            if (parsed && typeof parsed === "object") return [parsed];
        } catch (_e) {
            return [];
        }
    }
    return [];
}

function extractSalespersonArea(item) {
    if (!item) return "";
    const rawArea =
        item.sales_person_area ||
        item.area ||
        item.salesPersonArea ||
        item.salesperson_area ||
        "";
    if (typeof rawArea === "string") {
        return rawArea.trim();
    }
    if (rawArea == null) return "";
    return String(rawArea).trim();
}

function formatMeetingPerson(person) {
    if (person == null) return { name: "-", details: [] };
    if (typeof person === "string") return { name: person, details: [] };

    const name = person.meeting_person_name || person.meeting_name || person.person_name || person.name || "-";
    const details = [];
    const usedKeys = new Set(["meeting_person_name", "meeting_name", "person_name", "name"]);
    const knownFields = [
        { label: "Designation", keys: ["meeting_designation", "designation", "meeting_person_designation"] },
        { label: "Department", keys: ["meeting_department", "department"] },
        { label: "Mobile", keys: ["meeting_mobile", "mobile", "phone", "contact_no"] },
        { label: "Email", keys: ["meeting_email", "email", "mail"] },
        { label: "Company", keys: ["meeting_company", "company", "organization"] },
        { label: "Address", keys: ["meeting_address", "address"] },
    ];

    knownFields.forEach((field) => {
        const value = pickFirstNonEmpty(person, field.keys);
        if (value != null) {
            details.push(`${field.label}: ${value}`);
            field.keys.forEach((k) => usedKeys.add(k));
        }
    });

    Object.entries(person).forEach(([key, value]) => {
        if (usedKeys.has(key)) return;
        if (value == null || typeof value === "object") return;
        const text = String(value).trim();
        if (!text) return;
        details.push(`${toTitleLabel(key)}: ${text}`);
    });

    return { name: String(name), details };
}

function pickFirstNonEmpty(obj, keys) {
    for (const key of keys) {
        const value = obj?.[key];
        if (value == null) continue;
        const text = String(value).trim();
        if (text) return text;
    }
    return null;
}

function toTitleLabel(key) {
    return String(key || "")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (m) => m.toUpperCase());
}

function extractRecentVisits(payload) {
    const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.message)
            ? payload.message
            : Array.isArray(payload?.result)
              ? payload.result
              : [];
    return rows.filter((row) => row && (row.visit_date || row.customer_name));
}

async function fetchRecentSalesVisitsData(headers, limit = 300) {
    let lastError = null;
    for (const url of RECENT_SALES_VISIT_APIS) {
        try {
            return await axios.get(url, { headers, params: { include_all: 1, limit } });
        } catch (errGet) {
            lastError = errGet;
        }
        try {
            return await axios.post(url, { include_all: 1, limit }, { headers });
        } catch (errPost) {
            lastError = errPost;
        }
    }
    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load follow-up data."
    );
}

const mapStateToProps = (state) => ({
    authInfo: state.authInfoReducer,
});

export default connect(mapStateToProps)(FollowUps);
